import heic2any from 'heic2any';
import { ERROR_FALLBACKS, useUiStore } from '@/app/stores/ui';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';

export class PhotoProcessing {
  ALLOWED_FORMATS = {
    JPG: ['image/jpeg'],
    PNG: ['image/png'],
    WEBP: ['image/webp'],
    HEIC: ['image/heic', 'image/heif'],
  };

  FINAL_PHOTO_TYPE = 'image/jpeg';
  // JPEG quality 0.85 is visually indistinguishable from 0.95 for face detection
  // but produces ~50% smaller files (500-700 KB vs 1.5-2 MB at 1280px).
  // On slow kiosk networks (4G at 0.3-0.7 Mbps upload), this is the difference
  // between a 4-second upload and a 15-second timeout failure.
  FINAL_PHOTO_QUALITY = 0.85;
  FINAL_PHOTO_MAX_SIZE = 1920; // Max size in px by any side (1920x1080 full HD)

  WEBCAM_RESOLUTION_WIDTH = 2560;
  WEBCAM_RESOLUTION_HEIGHT = 1440;

  webcamContainerElement = null;
  webcamVideoElement = null;
  webcamZoom = 1;

  constructor() {
    exposeToGlobalScope(this, 'PhotoProcessing');
  }

  async initializeWebcam(
    webcamContainerElement,
    webcamVideoElement,
    webcamZoom
  ) {
    if (!(webcamContainerElement && webcamVideoElement)) {
      return;
    }

    this.webcamContainerElement = webcamContainerElement;
    this.webcamVideoElement = webcamVideoElement;
    this.setWebcamZoom(webcamZoom);

    sentryService.addBreadcrumb('photo.webcam', 'Initializing webcam', {
      requested_width: this.WEBCAM_RESOLUTION_WIDTH,
      requested_height: this.WEBCAM_RESOLUTION_HEIGHT,
      zoom: webcamZoom,
    });

    try {
      // Get the stream first, then check if element still exists
      // This guards against race condition where component unmounts during getUserMedia await
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: this.WEBCAM_RESOLUTION_WIDTH },
          height: { ideal: this.WEBCAM_RESOLUTION_HEIGHT },
        },
      });

      // Guard against race: component may have unmounted during getUserMedia await
      if (!this.webcamVideoElement) {
        stream.getTracks().forEach((track) => track.stop());
        console.warn(
          '[PhotoProcessing] Webcam element nullified during init, stream released'
        );
        return;
      }

      this.webcamVideoElement.srcObject = stream;

      // Track actual resolution obtained
      const videoTrack = stream.getVideoTracks()?.[0];
      const settings = videoTrack?.getSettings();
      if (settings) {
        sentryService.addBreadcrumb(
          'photo.webcam',
          'Webcam initialized successfully',
          {
            actual_width: settings.width,
            actual_height: settings.height,
            // Full device ID is intentional Sentry metadata for hardware joins.
            device_id: settings.deviceId,
          }
        );
      }
    } catch (e) {
      // Determine specific error type for better user feedback
      const errorName = e?.name || 'UnknownError';
      let errorCode = 'photo_webcam_not_allowed'; // default

      if (
        errorName === 'NotFoundError' ||
        errorName === 'DevicesNotFoundError'
      ) {
        errorCode = 'photo_webcam_not_found';
      } else if (
        errorName === 'NotReadableError' ||
        errorName === 'TrackStartError'
      ) {
        // Hardware busy, malfunctioning, or driver issue
        errorCode = 'photo_webcam_hardware_error';
      } else if (errorName === 'OverconstrainedError') {
        // Resolution not supported - fall back to default error
        errorCode = 'photo_webcam_not_allowed';
      }
      // NotAllowedError = permission denied (default)

      sentryService.captureException(e, {
        tags: {
          feature: 'photo_processing',
          operation: 'webcam_init',
          webcam_error_type: errorName,
        },
        extra: {
          requested_width: this.WEBCAM_RESOLUTION_WIDTH,
          requested_height: this.WEBCAM_RESOLUTION_HEIGHT,
          error_code: errorCode,
        },
      });
      // Use RETRY_CAMERA for recoverable hardware errors (busy, driver issue)
      // Use default HARD_RESTART for permission/not-found (retrying won't help)
      const fallback =
        errorCode === 'photo_webcam_hardware_error'
          ? ERROR_FALLBACKS.RETRY_CAMERA
          : undefined; // undefined = default HARD_RESTART
      useUiStore().showError(errorCode, e, fallback);
      throw e;
    }
  }

  stopWebcam() {
    const videoEl = this.webcamVideoElement;
    const stream = videoEl?.srcObject;
    if (stream) {
      const trackCount = stream.getTracks().length;

      // Pause video playback BEFORE stopping tracks. Some browser engines
      // keep the decoder running if the video element is still in "playing"
      // state even after srcObject is cleared, wasting CPU on the 24/7 kiosk.
      videoEl.pause();

      for (const track of stream.getTracks()) {
        track.stop();
      }
      videoEl.srcObject = null;
      sentryService.addBreadcrumb('photo.webcam', 'Webcam stopped', {
        tracks_stopped: trackCount,
      });
    }
    this.webcamVideoElement = null;
    // Also release the container reference to prevent retaining a stale
    // DOM node across sessions on the 24/7 kiosk.
    this.webcamContainerElement = null;
  }

  /**
   * Release all held references.  Called during session reset to ensure
   * large DOM nodes and media streams are not retained across sessions
   * on the 24/7 kiosk.
   */
  reset() {
    this.stopWebcam();
    this.webcamZoom = 1;
  }

  setWebcamZoom(webcamZoom) {
    this.webcamZoom = webcamZoom || 1;
    // Guard against null elements -- stopWebcam() or session reset may have
    // cleared references between initializeWebcam() and this call.
    if (!(this.webcamContainerElement && this.webcamVideoElement)) {
      return;
    }
    this.webcamContainerElement.style.overflow = 'hidden';
    this.webcamVideoElement.style.display = 'block';
    // Compose mirror + zoom transforms. scaleX(-1) mirrors for selfie-style
    // preview, and scale(zoom) crops the center. The CSS also sets scaleX(-1),
    // but this inline style overrides it, so we must include both here.
    this.webcamVideoElement.style.transform = `scaleX(-1) scale(${this.webcamZoom})`;
  }

  async getPreparedBlobFromWebcam() {
    if (!this.webcamVideoElement) {
      useUiStore().showError('photo_webcam_not_found');
      throw new Error('photo_webcam_not_found');
    }

    const startTime = performance.now();
    sentryService.addBreadcrumb('photo.capture', 'Starting webcam capture', {
      zoom: this.webcamZoom,
    });

    // Actual processing logic - extracted so it can run even if Sentry span fails
    const doWebcamCapture = async () => {
      // Re-check: webcamVideoElement may have been nulled by stopWebcam()
      // between the outer guard and this closure executing (async gap via withSpan).
      if (!this.webcamVideoElement) {
        throw new Error('photo_webcam_not_found');
      }

      const rawCanvas = this.drawMediaOnCanvas(this.webcamVideoElement);
      const originalWidth = rawCanvas.width;
      const originalHeight = rawCanvas.height;

      // Mirror the capture to match the mirrored preview (selfie-style).
      // Users see themselves mirrored in the preview, so the final photo
      // should also be mirrored for consistency.
      const canvas = this.mirrorCanvasHorizontally(rawCanvas);
      // Release raw capture canvas -- its pixels were copied into mirrored canvas
      this.releaseCanvas(rawCanvas);

      const zoomedCanvas = this.zoomAndCropWebcamCapture(
        canvas,
        this.webcamZoom
      );
      // Release mirrored canvas -- its pixels were copied into zoomedCanvas
      this.releaseCanvas(canvas);

      const resizedCanvas = this.shrinkLargeCanvas(zoomedCanvas);
      // Release zoom canvas -- its pixels were copied into resizedCanvas
      this.releaseCanvas(zoomedCanvas);

      // Capture dimensions BEFORE releaseCanvas zeroes them
      const finalWidth = resizedCanvas.width;
      const finalHeight = resizedCanvas.height;
      const blob = await this.canvasToJpegBlob(resizedCanvas);
      // Release the final canvas -- blob has been extracted
      this.releaseCanvas(resizedCanvas);

      const duration = performance.now() - startTime;
      sentryService.trackMetric(
        'photo.webcam_capture_duration_ms',
        duration,
        'millisecond'
      );
      sentryService.trackMetric(
        'photo.webcam_capture_size_bytes',
        blob.size,
        'byte'
      );
      sentryService.addBreadcrumb('photo.capture', 'Webcam capture completed', {
        original_dimensions: `${originalWidth}x${originalHeight}`,
        final_dimensions: `${finalWidth}x${finalHeight}`,
        blob_size: blob.size,
        duration_ms: duration.toFixed(0),
      });

      return blob;
    };

    try {
      return await sentryService.withSpan(
        'photo.webcam_capture',
        'image.processing',
        doWebcamCapture,
        {
          'photo.source': 'webcam',
          'photo.zoom': this.webcamZoom,
        }
      );
    } catch (spanError) {
      // If withSpan itself fails (Sentry unavailable), still do the processing
      if (
        spanError?.message?.includes('Sentry') ||
        spanError?.name === 'SentryError' ||
        !spanError?.stack?.includes('PhotoProcessing')
      ) {
        console.warn(
          '[PhotoProcessing] Sentry span failed, processing without tracing:',
          spanError.message
        );
        try {
          return await doWebcamCapture();
        } catch (processingError) {
          track(
            '[PhotoProcessing] Sentry capture failed:',
            () => {
              sentryService.captureException(processingError, {
                tags: {
                  feature: 'photo_processing',
                  operation: 'webcam_capture',
                },
                extra: { zoom: this.webcamZoom },
              });
            },
            warnMessageOf
          );
          useUiStore().showError('photo_webcam_photo_failed', processingError);
          throw processingError;
        }
      }
      // This is a real processing error from inside the span
      sentryService.captureException(spanError, {
        tags: { feature: 'photo_processing', operation: 'webcam_capture' },
        extra: { zoom: this.webcamZoom },
      });
      useUiStore().showError('photo_webcam_photo_failed', spanError);
      throw spanError;
    }
  }

  // Maximum files to decode in parallel.  Each file can require ~20-40 MB
  // of canvas memory (4 bytes/pixel for a 3000x2500 photo), so unbounded
  // parallelism on a kiosk with limited RAM causes OOM.
  MAX_PARALLEL_FILE_PROCESSING = 3;

  async getPreparedBlobFromMultipleFiles(files) {
    const fileArray = [...files];
    sentryService.addBreadcrumb('photo.batch', 'Processing multiple files', {
      file_count: fileArray.length,
      total_size: fileArray.reduce((sum, f) => sum + (f.size || 0), 0),
    });

    // Process files in batches to limit peak memory usage.
    // Without this, uploading 10+ HEIC files simultaneously could exhaust
    // GPU memory on the kiosk.
    const results = [];
    for (
      let i = 0;
      i < fileArray.length;
      i += this.MAX_PARALLEL_FILE_PROCESSING
    ) {
      const batch = fileArray.slice(i, i + this.MAX_PARALLEL_FILE_PROCESSING);
      const batchResults = await Promise.all(
        batch.map((file) => this.getPreparedBlobFromSingleFile(file))
      );
      results.push(...batchResults);
    }
    return results;
  }

  async getPreparedBlobFromSingleFile(file) {
    const fileFormat = this.detectSupportedFileFormat(file.type);
    if (!fileFormat) {
      sentryService.addBreadcrumb(
        'photo.file',
        'Unsupported file format rejected',
        {
          name: file?.name,
          size: file?.size,
          type: file?.type,
        },
        'warning'
      );
      useUiStore().showError(
        'photo_file_unsupported_type',
        {
          name: file?.name,
          size: file?.size,
          type: file?.type,
        },
        ERROR_FALLBACKS.CLOSE
      );
      throw new Error('photo_file_unsupported_type');
    }

    const startTime = performance.now();
    const originalSize = file?.size || 0;

    sentryService.addBreadcrumb('photo.file', 'Processing file', {
      name: file?.name,
      size: originalSize,
      type: file?.type,
      format: fileFormat,
    });

    const originalBlob = new Blob([file]);

    const shrinkToJpeg = async (blob, message, data) => {
      const canvas = await this.blobToCanvas(blob);
      const originalDimensions = `${canvas.width}x${canvas.height}`;
      const resizedCanvas = this.shrinkLargeCanvas(canvas);
      // Release decoded canvas -- pixels copied into resizedCanvas
      this.releaseCanvas(canvas);
      // Capture dimensions BEFORE releaseCanvas zeroes them
      const finalWidth = resizedCanvas.width;
      const finalHeight = resizedCanvas.height;
      const finalBlob = await this.canvasToJpegBlob(resizedCanvas);
      // Release resized canvas -- blob extracted
      this.releaseCanvas(resizedCanvas);

      const totalDuration = performance.now() - startTime;
      sentryService.trackMetric(
        'photo.file_process_duration_ms',
        totalDuration,
        'millisecond',
        { format: fileFormat.toLowerCase() }
      );
      sentryService.trackMetric(
        'photo.file_size_original_bytes',
        originalSize,
        'byte',
        { format: fileFormat.toLowerCase() }
      );
      sentryService.trackMetric(
        'photo.file_size_final_bytes',
        finalBlob.size,
        'byte',
        { format: fileFormat.toLowerCase() }
      );
      sentryService.incrementCounter('photo.files_processed', 1, {
        format: fileFormat.toLowerCase(),
      });

      sentryService.addBreadcrumb('photo.file', message, {
        ...data,
        original_size: originalSize,
        final_size: finalBlob.size,
        original_dimensions: originalDimensions,
        final_dimensions: `${finalWidth}x${finalHeight}`,
        compression_ratio: (originalSize / finalBlob.size).toFixed(2),
        duration_ms: totalDuration.toFixed(0),
      });

      return finalBlob;
    };

    if (fileFormat === 'HEIC') {
      // Actual HEIC processing logic - extracted so it can run even if Sentry span fails
      const doHeicProcessing = async () => {
        const heicStartTime = performance.now();
        const jpegBlob = await this.heicToJpegBlob(originalBlob);
        const heicDuration = performance.now() - heicStartTime;

        sentryService.trackMetric(
          'photo.heic_conversion_duration_ms',
          heicDuration,
          'millisecond'
        );
        sentryService.addBreadcrumb('photo.heic', 'HEIC converted to JPEG', {
          original_size: originalSize,
          converted_size: jpegBlob.size,
          duration_ms: heicDuration.toFixed(0),
        });

        return shrinkToJpeg(jpegBlob, 'HEIC file processed successfully', {});
      };

      try {
        return await sentryService.withSpan(
          'photo.process_heic',
          'image.processing',
          doHeicProcessing,
          {
            'photo.source': 'file',
            'photo.format': 'heic',
            'photo.original_size': originalSize,
          }
        );
      } catch (spanError) {
        // If withSpan itself fails (Sentry unavailable), still do the processing
        if (
          spanError?.message?.includes('Sentry') ||
          spanError?.name === 'SentryError' ||
          !spanError?.stack?.includes('PhotoProcessing')
        ) {
          console.warn(
            '[PhotoProcessing] Sentry span failed, processing without tracing:',
            spanError.message
          );
          try {
            return await doHeicProcessing();
          } catch (processingError) {
            track(
              '[PhotoProcessing] Sentry capture failed:',
              () => {
                sentryService.captureException(processingError, {
                  tags: {
                    feature: 'photo_processing',
                    operation: 'heic_conversion',
                  },
                  extra: {
                    file_name: file?.name,
                    file_size: originalSize,
                    file_type: file?.type,
                  },
                });
              },
              warnMessageOf
            );
            useUiStore().showError(
              'photo_file_cannot_process_heic',
              processingError,
              ERROR_FALLBACKS.CLOSE
            );
            throw processingError;
          }
        }
        // This is a real processing error from inside the span
        sentryService.captureException(spanError, {
          tags: { feature: 'photo_processing', operation: 'heic_conversion' },
          extra: {
            file_name: file?.name,
            file_size: originalSize,
            file_type: file?.type,
          },
        });
        useUiStore().showError(
          'photo_file_cannot_process_heic',
          spanError,
          ERROR_FALLBACKS.CLOSE
        );
        throw spanError;
      }
    } else {
      // Actual file processing logic - extracted so it can run even if Sentry span fails
      const doFileProcessing = () =>
        shrinkToJpeg(originalBlob, 'File processed successfully', {
          format: fileFormat,
        });

      try {
        return await sentryService.withSpan(
          'photo.process_file',
          'image.processing',
          doFileProcessing,
          {
            'photo.source': 'file',
            'photo.format': fileFormat.toLowerCase(),
            'photo.original_size': originalSize,
          }
        );
      } catch (spanError) {
        // If withSpan itself fails (Sentry unavailable), still do the processing
        if (
          spanError?.message?.includes('Sentry') ||
          spanError?.name === 'SentryError' ||
          !spanError?.stack?.includes('PhotoProcessing')
        ) {
          console.warn(
            '[PhotoProcessing] Sentry span failed, processing without tracing:',
            spanError.message
          );
          try {
            return await doFileProcessing();
          } catch (processingError) {
            track(
              '[PhotoProcessing] Sentry capture failed:',
              () => {
                sentryService.captureException(processingError, {
                  tags: {
                    feature: 'photo_processing',
                    operation: 'file_process',
                  },
                  extra: {
                    file_name: file?.name,
                    file_size: originalSize,
                    file_type: file?.type,
                    file_format: fileFormat,
                  },
                });
              },
              warnMessageOf
            );
            useUiStore().showError(
              'photo_file_cannot_process',
              processingError,
              ERROR_FALLBACKS.CLOSE
            );
            throw processingError;
          }
        }
        // This is a real processing error from inside the span
        sentryService.captureException(spanError, {
          tags: { feature: 'photo_processing', operation: 'file_process' },
          extra: {
            file_name: file?.name,
            file_size: originalSize,
            file_type: file?.type,
            file_format: fileFormat,
          },
        });
        useUiStore().showError(
          'photo_file_cannot_process',
          spanError,
          ERROR_FALLBACKS.CLOSE
        );
        throw spanError;
      }
    }
  }

  detectSupportedFileFormat(fileType) {
    for (const fileFormat of Object.keys(this.ALLOWED_FORMATS)) {
      const mimeTypes = this.ALLOWED_FORMATS[fileFormat];
      if (mimeTypes.includes(fileType.toLowerCase())) {
        return fileFormat;
      }
    }
    return null;
  }

  drawMediaOnCanvas(mediaElement) {
    const startTime = performance.now();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const width =
      mediaElement.videoWidth ||
      mediaElement.naturalWidth ||
      mediaElement.width;
    const height =
      mediaElement.videoHeight ||
      mediaElement.naturalHeight ||
      mediaElement.height;

    // Guard against 0x0 dimensions: video not yet loaded, or element has no
    // intrinsic size.  A 0x0 canvas produces a null blob from toBlob() which
    // cascades into confusing errors downstream.
    if (!(width && height)) {
      throw new Error(
        `Cannot draw media on canvas: source has zero dimensions ` +
          `(${width}x${height}). Video may not be loaded yet.`
      );
    }

    canvas.width = width;
    canvas.height = height;

    ctx.drawImage(mediaElement, 0, 0, width, height);

    // Track canvas operation time
    const duration = performance.now() - startTime;
    try {
      sentryService.trackMetric(
        'canvas.draw_duration_ms',
        duration,
        'millisecond',
        {
          operation: 'drawMediaOnCanvas',
          width: canvas.width,
          height: canvas.height,
        }
      );
    } catch (e) {
      // Non-critical - silently ignore Sentry failures
    }

    return canvas;
  }

  canvasToJpegBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(
              new Error(
                `Canvas toBlob returned null (${canvas.width}x${canvas.height}, ` +
                  `tainted canvas or unsupported codec)`
              )
            );
            return;
          }
          resolve(blob);
        },
        this.FINAL_PHOTO_TYPE,
        this.FINAL_PHOTO_QUALITY
      );
    });
  }

  blobToCanvas(blob) {
    return new Promise((resolve, reject) => {
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();

      const cleanup = () => {
        URL.revokeObjectURL(blobUrl);
      };

      img.onload = () => {
        const canvas = this.drawMediaOnCanvas(img);
        cleanup();
        resolve(canvas);
      };
      img.onerror = (_event) => {
        cleanup();
        // img.onerror receives an Event, not an Error.  Wrap it so
        // callers get a proper Error with a useful message and stack.
        reject(
          new Error(
            `Failed to decode image blob (size=${blob?.size || 'unknown'}, ` +
              `type=${blob?.type || 'unknown'})`
          )
        );
      };
      img.src = blobUrl;
    });
  }

  shrinkLargeCanvas(canvas) {
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;

    let scaleFactor = 1;
    if (originalWidth > originalHeight) {
      if (originalWidth > this.FINAL_PHOTO_MAX_SIZE) {
        scaleFactor = this.FINAL_PHOTO_MAX_SIZE / originalWidth;
      }
    } else if (originalHeight > this.FINAL_PHOTO_MAX_SIZE) {
      scaleFactor = this.FINAL_PHOTO_MAX_SIZE / originalHeight;
    }

    const newWidth = originalWidth * scaleFactor;
    const newHeight = originalHeight * scaleFactor;

    const resizedCanvas = document.createElement('canvas');
    const ctx = resizedCanvas.getContext('2d');

    resizedCanvas.width = newWidth;
    resizedCanvas.height = newHeight;

    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);

    return resizedCanvas;
  }

  /**
   * Release a canvas element's GPU/memory backing store.
   * On iOS Safari and some WebKit builds, canvas memory is not freed by GC
   * until width and height are set to 0.  On a 24/7 kiosk processing many
   * photos, failing to do this causes unbounded GPU memory growth.
   * @param {...HTMLCanvasElement} canvases - One or more canvas elements to release
   */
  releaseCanvas(...canvases) {
    for (const canvas of canvases) {
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    }
  }

  async heicToJpegBlob(heicBlob) {
    const result = await heic2any({
      blob: heicBlob,
      toType: this.FINAL_PHOTO_TYPE,
      quality: this.FINAL_PHOTO_QUALITY,
    });
    // heic2any returns Blob | Blob[] - normalize to single Blob
    return Array.isArray(result) ? result[0] : result;
  }

  zoomAndCropWebcamCapture(canvas, zoom = 1) {
    const zoomAndCropCanvas = document.createElement('canvas');
    const zoomAndCropCtx = zoomAndCropCanvas.getContext('2d');
    const { width: originalWidth, height: originalHeight } = canvas;

    const scaledWidth = originalWidth / zoom;
    const scaledHeight = originalHeight / zoom;

    zoomAndCropCanvas.width = originalWidth;
    zoomAndCropCanvas.height = originalHeight;

    const sx = (originalWidth - scaledWidth) / 2;
    const sy = (originalHeight - scaledHeight) / 2;
    const dx = 0;
    const dy = 0;

    zoomAndCropCtx.drawImage(
      canvas,
      sx,
      sy,
      scaledWidth,
      scaledHeight,
      dx,
      dy,
      originalWidth,
      originalHeight
    );
    return zoomAndCropCanvas;
  }

  /**
   * Mirror a canvas horizontally (flip on the Y axis).
   * Used for webcam captures so the final photo matches the mirrored preview.
   * @param {HTMLCanvasElement} canvas - The canvas to mirror
   * @returns {HTMLCanvasElement} - A new canvas with the mirrored image
   */
  mirrorCanvasHorizontally(canvas) {
    const mirroredCanvas = document.createElement('canvas');
    const ctx = mirroredCanvas.getContext('2d');
    const { width, height } = canvas;

    mirroredCanvas.width = width;
    mirroredCanvas.height = height;

    // Flip horizontally: translate to right edge, scale X by -1
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(canvas, 0, 0);

    return mirroredCanvas;
  }
}

export const photoProcessing = new PhotoProcessing();
